/**
 * L7: the codex (ทะเบียนสัตว์ร้าย) and the signature drops (docs/design/2026-10-11-gameplay-loop.md, decision Q3).
 * One family per kind of beast (monster ids that share a family count together). Kill tiers 10 / 50 / 200 each pay once:
 * a permanent damage bonus against that family and silver. Each family also has one signature item: about 1 % per kill
 * (x3 from an elite), carrying one unique affix line. Placeholder icons: the item's slot icon. Data only.
 */
import type { AffixRoll } from './affixes';
import type { ItemDef } from './items';

export interface CodexFamily {
  readonly id: string;
  readonly name: string;
  /** Monster ids (data/monsters.ts) that count for this family. */
  readonly members: readonly string[];
}

/** Kill counts that pay, and what each pays. */
export const CODEX_TIERS: readonly { readonly kills: number; readonly dmg: number; readonly silver: number }[] = [
  { kills: 10, dmg: 0.02, silver: 50 },
  { kills: 50, dmg: 0.04, silver: 200 },
  { kills: 200, dmg: 0.06, silver: 800 },
];

/** Base signature drop chance per kill, and the elite multiplier. */
export const SIGNATURE = { chance: 0.01, eliteMul: 3 } as const;

interface Row {
  readonly fam: string;
  readonly name: string;
  readonly members: readonly string[];
  readonly item: string;
  readonly itemName: string;
  readonly desc: string;
  readonly affix: string;
  readonly v: number;
}

const rows: Row[] = [
  { fam: 'wolf', name: 'หมาป่า', members: ['wolf'], item: 'sig-wolf', itemName: 'เขี้ยวเงาจันทร์', desc: 'เขี้ยวหมาป่าเงาจันทร์ที่ร้อยเชือก', affix: 'bleedChance', v: 0.08 },
  { fam: 'bandit', name: 'โจรป่า', members: ['bandit'], item: 'sig-bandit', itemName: 'ป้ายหัวโจรเก่า', desc: 'ป้ายไม้แกะรอยมีดของเหล่าโจรป่า', affix: 'crit', v: 0.03 },
  { fam: 'masked', name: 'ชายสวมหน้ากาก', members: ['masked', 'masked-chief'], item: 'sig-masked', itemName: 'ชายผ้าปิดหน้าลายคราม', desc: 'ผ้าปิดหน้าที่ฉีกจากชายลึกลับ', affix: 'dodge', v: 6 },
  { fam: 'assassin', name: 'มือสังหาร', members: ['assassin'], item: 'sig-assassin', itemName: 'เข็มเงียบเสียงลม', desc: 'เข็มสั้นของมือสังหารที่ไร้เสียง', affix: 'defPierce', v: 0.04 },
  { fam: 'monk', name: 'พระนอกรีต', members: ['monk'], item: 'sig-monk', itemName: 'ลูกประคำร้าวหนึ่งเม็ด', desc: 'ลูกประคำที่ร้าวจากการฝึกผิดทาง', affix: 'maxQi', v: 30 },
  { fam: 'bear', name: 'หมีภูเขา', members: ['bear'], item: 'sig-bear', itemName: 'กรงเล็บหมีหินผา', desc: 'กรงเล็บหนักอึ้งเหมือนก้อนหิน', affix: 'maxHp', v: 40 },
  { fam: 'knifer', name: 'นักมีด', members: ['knifer'], item: 'sig-knifer', itemName: 'ด้ามมีดพันผ้าแดง', desc: 'ด้ามมีดที่พันผ้าแดงซีดจนเก่า', affix: 'atk', v: 5 },
  { fam: 'jiangshi', name: 'ซากกระโดด', members: ['jiangshi'], item: 'sig-jiangshi', itemName: 'ยันต์เหลืองหน้าผาก', desc: 'ยันต์ที่ติดหน้าผากซากกระโดดไว้', affix: 'poisonRes', v: 0.1 },
  { fam: 'snowwolf', name: 'หมาป่าหิมะ', members: ['snowwolf'], item: 'sig-snowwolf', itemName: 'ขนขาวต้านลมหนาว', desc: 'ขนสีขาวที่อุ่นแม้ลมหนาวจัด', affix: 'speed', v: 0.04 },
  { fam: 'frostman', name: 'คนน้ำแข็ง', members: ['frostman'], item: 'sig-frostman', itemName: 'ผลึกใจน้ำแข็ง', desc: 'ผลึกเย็นเฉียบจากอกของคนน้ำแข็ง', affix: 'magicDef', v: 8 },
  { fam: 'boar', name: 'หมูป่า', members: ['boar'], item: 'sig-boar', itemName: 'งาหมูป่าดื้อรั้น', desc: 'งาสั้นแต่แข็งแรงของหมูป่า', affix: 'toughness', v: 8 },
  { fam: 'viper', name: 'งูพิษ', members: ['viper'], item: 'sig-viper', itemName: 'ต่อมพิษงูเขียว', desc: 'ต่อมพิษที่ผนึกไว้ในขวดเล็ก', affix: 'poisonChance', v: 0.1 },
  { fam: 'scorpion', name: 'แมงป่อง', members: ['scorpion'], item: 'sig-scorpion', itemName: 'หางแมงป่องทราย', desc: 'ปลายหางแห้งที่ยังแหลมคม', affix: 'hit', v: 6 },
  { fam: 'leopard', name: 'เสือดาว', members: ['leopard'], item: 'sig-leopard', itemName: 'หนังลายจุดไร้เสียง', desc: 'หนังเสือดาวนิ่มจนก้าวเท้าไร้เสียง', affix: 'maxStamina', v: 25 },
  { fam: 'bloodbat', name: 'ค้างคาวเลือด', members: ['bloodbat'], item: 'sig-bloodbat', itemName: 'ปีกค้างคาวสีเลือด', desc: 'ปีกบางสีเลือดที่ยังอุ่นอยู่', affix: 'lifesteal', v: 0.02 },
  { fam: 'spider', name: 'แมงมุมพิษ', members: ['spider'], item: 'sig-spider', itemName: 'ใยแมงมุมเหนียวแน่น', desc: 'เส้นใยเหนียวที่ฉีกไม่ขาด', affix: 'stunPower', v: 0.1 },
  { fam: 'centipede', name: 'ตะขาบ', members: ['centipede'], item: 'sig-centipede', itemName: 'ข้อตะขาบร้อยขา', desc: 'ข้อเปลือกแข็งที่ยังกระดิกได้', affix: 'def', v: 5 },
  { fam: 'python', name: 'งูเหลือม', members: ['python'], item: 'sig-python', itemName: 'เกล็ดงูรัดแน่น', desc: 'เกล็ดใหญ่ที่เรียงซ้อนเหมือนเกราะ', affix: 'bleedRes', v: 0.1 },
  { fam: 'foxspirit', name: 'จิ้งจอกวิญญาณ', members: ['foxspirit'], item: 'sig-foxspirit', itemName: 'หางจิ้งจอกเก้าสี', desc: 'ปลายหางที่เปลี่ยนสีเมื่อต้องแสงจันทร์', affix: 'magic', v: 5 },
  { fam: 'whitetiger', name: 'พยัคฆ์ขาว', members: ['whitetiger'], item: 'sig-whitetiger', itemName: 'เขี้ยวพยัคฆ์ขาว', desc: 'เขี้ยวใหญ่ของเจ้าป่าสีหิมะ', affix: 'critDmg', v: 0.08 },
];

export const CODEX_FAMILIES: readonly CodexFamily[] = rows.map((r) => ({ id: r.fam, name: r.name, members: r.members }));
const OF_MONSTER: Readonly<Record<string, string>> = Object.fromEntries(
  rows.flatMap((r) => r.members.map((m) => [m, r.fam] as const)),
);
/** The codex family of a monster id, or undefined (dummies, bosses and sparring foes are not listed). */
export const codexFamilyOf = (monsterId: string): string | undefined => OF_MONSTER[monsterId];

/** The signature item of a family, and the fixed affix line it carries (placeholder icon: the pendant's). */
export const SIGNATURE_ITEMS: readonly ItemDef[] = rows.map((r) => ({
  id: r.item,
  name: r.itemName,
  slot: 'pendant',
  rarity: 'rare',
  ilv: 12,
  desc: `${r.desc} (ของประจำตัว${r.name})`,
  stats: {},
  visual: '',
  worth: 400,
}));
export const SIGNATURE_AFFIX: Readonly<Record<string, AffixRoll>> = Object.fromEntries(
  rows.map((r) => [r.item, { id: r.affix, v: r.v, q: 100, imp: true } as AffixRoll]),
);
export const SIGNATURE_OF_FAMILY: Readonly<Record<string, string>> = Object.fromEntries(rows.map((r) => [r.fam, r.item]));
